import { Injectable } from '@angular/core';
import { SvgEditExtension, SvgEditExtensionObject } from '@models/svg-edit-extension';
import { CanvasService } from './canvas.service';
import { CanvasInputMode, CanvasInputModeService } from './canvas-input-mode.service';
import { SelectedElementsService } from './selected-elements.service';
import { Channel } from 'icreate/common/models/channel';
import { DefaultColorName, SystemColor } from 'icreate/common/models/system-color';
import {
    FLAG_UNDEFINED_COLOR,
    FlagOption,
    getAlarmStateColor,
    GRAPH_UNDEFINED_COLOR,
} from '@service/graphs/print-gauge-graph-utils';
import { BarScaleType, BarSetting } from 'icreate/common/models/editor-viewer-element';
import { ElementAttribute } from '@src/app/models/editor';

@Injectable({ providedIn: 'root' })
export class BarExtensionService implements SvgEditExtension {
    name: string = 'statusIndicatorExtension';

    constructor(private plugin: BarExtensionPlugin) {
        this.init = this.init.bind(this);
    }

    init(): SvgEditExtensionObject {
        this.bindMouseEvents();
        return this.plugin;
    }

    addEventListeners(foreignObject) {
        this.plugin.addEventListeners(foreignObject);
    }

    setUnitsVisible(element, visible) {
        this.plugin.setUnitsVisible(element, visible);
    }

    setValueVisible(element, visible) {
        this.plugin.setValueVisible(element, visible);
    }

    setNameVisible(element, visible) {
        this.plugin.setNameVisible(element, visible);
    }

    updateDisplay(element, channel: Channel, barSetting: BarSetting, systemColors: SystemColor[]) {
        this.plugin.updateDisplay(element, channel, barSetting, systemColors);
    }

    private bindMouseEvents() {
        return this.plugin.bindMouseEvents();
    }
}

@Injectable({ providedIn: 'root' })
class BarExtensionPlugin implements SvgEditExtensionObject {
    constructor(
        private canvasService: CanvasService,
        private inputMode: CanvasInputModeService,
        private selectedElementsService: SelectedElementsService
    ) {}

    readonly updateDisplayEventName = 'updateDisplay';
    readonly updateValueEventName = 'valueUpdated';

    static readonly XHTML = 'http://www.w3.org/1999/xhtml';

    static readonly DESIGN_WIDTH = 111;
    static readonly GAP = 4;
    static readonly LABELS_W = 27.5;
    static readonly METER_W = 24;
    static readonly FONT_SIZE = 8;
    static readonly TICK_COUNT = 7;

    private applyStyle(el: HTMLElement, style: Partial<CSSStyleDeclaration>): HTMLElement {
        Object.assign(el.style, style);
        return el;
    }

    private styleLabelsStyle(foreignObject) {
        const width = parseFloat(foreignObject.getAttribute('width') ?? '0');
        const scale = width / BarExtensionPlugin.DESIGN_WIDTH;
        const labelsContainer = foreignObject.querySelector('[labelsContainer="true"]') as HTMLElement;
        labelsContainer.style.flex = '0 0 auto';
        labelsContainer.style.flexDirection = 'column';
        labelsContainer.style.justifyContent = 'space-between';
        labelsContainer.style.alignItems = 'flex-end';
        labelsContainer.style.width = `${BarExtensionPlugin.LABELS_W * scale}px`;
        labelsContainer.style.padding = '0';

        const fontSize = `${BarExtensionPlugin.FONT_SIZE * scale}px`;
        labelsContainer.querySelectorAll<HTMLElement>('[labelValue="true"]').forEach(label => {
            label.style.display = 'flex';
            label.style.alignItems = 'center';
            label.style.justifyContent = 'flex-end';
            label.style.textAlign = 'right';
            label.style.whiteSpace = 'nowrap';
            label.style.flex = 'none';
            label.style.fontFamily = "'Noto Sans', sans-serif";
            label.style.fontStyle = 'normal';
            label.style.fontWeight = '500';
            label.style.fontSize = fontSize;
            label.style.lineHeight = `${7 * scale}px`;
            label.style.color = 'rgba(255, 255, 255, 0.7)';
        });
    }

    private div(name: string, style: Partial<CSSStyleDeclaration>): HTMLDivElement {
        const el = document.createElement('div');
        el.setAttribute('xmlns', BarExtensionPlugin.XHTML);
        el.setAttribute('data-part', name);
        this.applyStyle(el, { boxSizing: 'border-box', ...style });
        return el;
    }

    callback(deps: any) {}

    bindMouseEvents() {
        document.addEventListener('mousedown', this.handleMouseDown.bind(this));
        document.addEventListener('mousemove', this.handleMouseMove.bind(this));
        document.addEventListener('mouseup', this.handleMouseUp.bind(this));
    }

    private handleMouseDown(event: MouseEvent) {}

    private handleMouseMove(event: MouseEvent) {}

    private handleMouseUp(event: MouseEvent) {
        if (this.inputMode.getMode() === CanvasInputMode.BAR) {
            const svgRoot = this.canvasService.getSvgRoot() as unknown as Element;
            if (!svgRoot || typeof svgRoot !== 'object') return;
            if (event.target !== svgRoot && !(svgRoot as Element).contains(event.target as Node)) {
                return;
            }
            const canvas = this.canvasService.getCanvas();

            const rootSctm = this.canvasService.getSCTM();
            const pt = this.canvasService.transformPoint(event.clientX, event.clientY, rootSctm);
            const mouseX = pt.x;
            const mouseY = pt.y;

            const foreignObject = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
            foreignObject.id = canvas.getNextId();
            foreignObject.setAttribute('x', mouseX.toString());
            foreignObject.setAttribute('y', mouseY.toString());
            foreignObject.setAttribute('type', CanvasInputMode.BAR);
            foreignObject.setAttribute('width', '111');
            foreignObject.setAttribute('height', '292');
            foreignObject.setAttribute('pointer-events', 'all');

            const container = this.div('linearGaugeRoot', {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0px',
                gap: '6px',
                width: '100%',
                height: '100%',
            });
            container.setAttribute('linearGaugeRoot', 'true');

            container.setAttribute(ElementAttribute.barScaleType, BarScaleType.SINGLE);
            container.setAttribute(ElementAttribute.barIntervals, '0');

            const gauge = this.div('linearGauge', {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'stretch',
                padding: '0px',
                gap: '4px',
                width: '100%',
                flex: '1 1 0',
                minHeight: '0',
            });
            gauge.setAttribute('gaugeRow', 'true');

            const labels = this.div('labels', {
                display: 'none',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                padding: '0px',
                width: '27.5px',
                flex: 'none',
            });
            labels.setAttribute('labelsContainer', 'true');

            const meter = this.div('meter', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                padding: '0px',
                isolation: 'isolate',
                position: 'relative',
                width: '24px',
                height: '100%',
                flex: 'none',
            });
            meter.setAttribute('meterContainer', 'true');

            const bar = this.div('bar', {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'stretch',
                padding: '0px',
                isolation: 'isolate',
                position: 'relative',
                width: '18px',
                height: '100%',
                flex: 'none',
                zIndex: '0',
            });

            const barTrack = this.div('barTrack', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'stretch',
                padding: '0px',
                position: 'absolute',
                width: '18px',
                height: '100%',
                left: '0px',
                top: '0px',
                zIndex: '0',
            });

            const ticks = this.div('ticks', {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                padding: '0px',
                width: '6px',
                flex: 'none',
            });
            ticks.setAttribute('ticksContainer', 'true');

            const base = this.div('base', {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: '0px',
                width: '12px',
                flex: 'none',
                backgroundColor: '#4A5C70',
                borderRadius: '0px 4px 4px 0px',
            });
            base.setAttribute('baseContainer', 'true');

            barTrack.append(ticks, base);

            const ranges = this.div('ranges', {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                padding: '0px',
                position: 'absolute',
                left: '6px',
                top: '0px',
                width: '12px',
                height: '100%',
                borderRadius: '0px 4px 4px 0px',
                zIndex: '1',
            });
            ranges.setAttribute('rangesContainer', 'true');

            const indicator = this.div('indicator', {
                display: 'flex',
                position: 'absolute',
                width: '17px',
                height: '19px',
                left: '7px',
                top: '50%',
                backgroundColor: '#FF3B30',
                transform: 'translateY(-50%)',
                clipPath: 'polygon(100% 0, 0 50%, 100% 100%)',
                zIndex: '1',
            });
            indicator.setAttribute('indicatorPointer', 'true');

            bar.append(barTrack, ranges);
            meter.append(bar, indicator);
            gauge.append(labels, meter);

            const currentValue = this.div('currentValueBlock', {
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                padding: '0px',
                width: '100%',
                flex: 'none',
            });

            const value = this.div('currentValue', {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '8px',
                lineHeight: '10px',
                color: '#ffffff',
            });
            value.textContent = '- (Units)';
            value.setAttribute('valueContainer', 'true');

            const channel = this.div('channel', {
                display: 'none',
                alignItems: 'center',
                width: '100%',
                fontFamily: 'Inter, sans-serif',
                fontStyle: 'normal',
                fontWeight: '400',
                fontSize: '8px',
                lineHeight: '10px',
                color: 'rgba(255, 255, 255, 0.7)',
            });
            channel.setAttribute('channelNameContainer', 'true');
            channel.textContent = 'Channel';

            currentValue.append(channel, value);

            container.append(gauge, currentValue);
            foreignObject.appendChild(container);

            this.applyRanges(ranges, null, null);
            this.applyTicks(foreignObject, null, null);
            this.styleLabelsStyle(foreignObject);

            const layer = (svgRoot as Element).querySelector('.layer') as Element;
            if (layer) {
                layer.appendChild(foreignObject);
            }

            this.addEventListeners(foreignObject);
            canvas.addCommandToHistory(new canvas.history.InsertElementCommand(foreignObject));
            this.selectedElementsService.setSelectedElements([foreignObject]);
            this.inputMode.setMode(CanvasInputMode.SELECT);
        }
    }

    private applyRanges(rangesContainer, channel: Channel, systemColors?: SystemColor[]) {
        if (!channel) {
            rangesContainer.replaceChildren();
            const rangeTop = this.div('range', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                padding: '1px 1px 0.5px',
                width: '100%',
                flex: '1 1 0',
            });
            rangeTop.appendChild(
                this.div('rangeFill', {
                    width: '2px',
                    alignSelf: 'stretch',
                    backgroundColor: 'rgba(156, 178, 251, 0.7)',
                    borderRadius: '2px 2px 0px 0px',
                    flex: 'none',
                })
            );

            const rangeBottom = this.div('range', {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                padding: '0.5px 1px 1px',
                width: '100%',
                flex: '1 1 0',
            });
            rangeBottom.appendChild(
                this.div('rangeFill', {
                    width: '2px',
                    alignSelf: 'stretch',
                    backgroundColor: 'rgba(255, 59, 48, 0.7)',
                    borderRadius: '0px 0px 2px 2px',
                    flex: 'none',
                })
            );
            rangesContainer.append(rangeTop, rangeBottom);
        } else {
            const measurableRange = channel.fcuData.measurableRange;
            const rangeMax = this.scaleValue(channel, measurableRange.max);
            const rangeMin = this.scaleValue(channel, measurableRange.min);
            const totalRange = Math.abs(rangeMax - rangeMin);
            const ranges = this.calculateRanges(channel, rangeMin, rangeMax);
            const alarmColors = {
                [DefaultColorName.ALARM]: systemColors.find(color => color.name == DefaultColorName.ALARM)?.color,
                [DefaultColorName.CAUTION]: systemColors.find(color => color.name == DefaultColorName.CAUTION)?.color,
                [DefaultColorName.WARNING]: systemColors.find(color => color.name == DefaultColorName.WARNING)?.color,
                [FlagOption.NORMAL]: FLAG_UNDEFINED_COLOR,
            };

            const colorArr: [number, string][] = [];
            let previewRange = 0;
            let rangeKeys = Array.from(ranges.keys());

            for (let i = 0; i < rangeKeys.length; i++) {
                let range = ranges.get(rangeKeys[i]);
                const width = range.max - range.min;
                if (width > 0) {
                    let percentage = previewRange + (range.max - range.min) / totalRange;
                    let color = getAlarmStateColor(rangeKeys[i], channel, alarmColors);
                    if (color == GRAPH_UNDEFINED_COLOR) color = null;
                    if (rangeKeys[i] === FlagOption.NORMAL) color = FLAG_UNDEFINED_COLOR;
                    colorArr.push([percentage, color]);
                    previewRange = percentage;
                }
            }

            rangesContainer.replaceChildren();

            let prev = 0;
            colorArr.forEach(([cumulative, color], i) => {
                const fraction = cumulative - prev;
                prev = cumulative;
                if (fraction <= 0) return;

                const isFirst = i === 0;
                const isLast = i === colorArr.length - 1;

                const segment = this.div('range', {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                    width: '100%',
                    flex: `${fraction} 1 0`,
                    minHeight: '0',
                    padding: '0px 1px',
                    boxSizing: 'border-box',
                });

                segment.appendChild(
                    this.div('rangeFill', {
                        width: '2px',
                        alignSelf: 'stretch',
                        backgroundColor: color ? color : 'unset',
                        borderRadius: `${isLast ? '2px' : '0'} ${isLast ? '2px' : '0'} ${isFirst ? '2px' : '0'} ${isFirst ? '2px' : '0'}`,
                        flex: 'none',
                    })
                );
                rangesContainer.appendChild(segment);
            });
        }
    }

    private applyTicks(foreignObject, channel: Channel, barSetting) {
        const ticksContainer = foreignObject.querySelector('[ticksContainer="true"]') as HTMLElement;
        const baseContainer = foreignObject.querySelector('[baseContainer="true"]') as HTMLElement;
        const labelsContainer = foreignObject.querySelector('[labelsContainer="true"]') as HTMLElement;
        const linearGaugeRoot = foreignObject.querySelector('[linearGaugeRoot="true"]') as HTMLElement;

        const scaleType = linearGaugeRoot.getAttribute(ElementAttribute.barScaleType);
        const numOfTicks = Number(linearGaugeRoot.getAttribute(ElementAttribute.barIntervals));
        let numDivision = channel ? 2 : 7;
        if (scaleType == BarScaleType.ZERO) numDivision = numOfTicks * 2 + 2 + 1;
        else if (scaleType == BarScaleType.SINGLE) numDivision = numOfTicks + 2;

        ticksContainer.replaceChildren();
        baseContainer.replaceChildren();

        for (let i = 0; i < numDivision; i++) {
            ticksContainer.appendChild(
                this.div('tick', {
                    width: '6px',
                    height: '1px',
                    backgroundColor: '#4A5C70',
                    borderRadius: '180px 0px 0px 180px',
                    flex: 'none',
                })
            );
        }
        const intervalBorder = '0.5px solid #252D39';
        for (let i = 0; i < numDivision - 1; i++) {
            const isFirst = i === 0;
            const isLast = i === numDivision - 1;
            baseContainer.appendChild(
                this.div('interval', {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: isLast ? 'flex-start' : 'flex-end',
                    padding: isLast ? '0.5px 1px 1px' : '1px 1px 0.5px',
                    gap: '5px',
                    width: '100%',
                    flex: '1 1 0',
                    borderTop: isFirst ? 'none' : intervalBorder,
                    borderBottom: isLast ? 'none' : intervalBorder,
                })
            );
        }
        labelsContainer.replaceChildren();
        const labels = [];
        if (channel) {
            const maxRange = this.scaleValue(channel, channel.fcuData.measurableRange.max);
            const minRange = this.scaleValue(channel, channel.fcuData.measurableRange.min);
            const units = channel.opsData.measurementUnit;
            for (let i = 0; i < numDivision; i++) {
                if (i == 0) labels.push(maxRange);
                else if (i + 1 == numDivision) labels.push(`${minRange} (${units})`);
                else {
                    const totalValue = maxRange - minRange;
                    const value = (totalValue / numDivision) * i + 1;
                    labels.push(value.toFixed(2));
                }
            }
        }

        labels.forEach(text => {
            const label = document.createElement('div');
            label.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');
            label.setAttribute('labelValue', 'true');
            label.textContent = text;
            labelsContainer.appendChild(label);
        });

        labelsContainer.style.display = channel ? 'flex' : 'none';
    }

    private scaleValue(channel: Channel, value: number) {
        return value / channel.fcuData.scale;
    }

    private calculateRanges(channel, rangeMin: number, rangeMax: number) {
        const fcuAlarms = channel.fcuData;
        const rangeOffset = 1 / channel.fcuData.scale;
        const rangeMap = new Map();

        const toRange = (min: number, max: number, zeroOut: boolean = false): { min: number; max: number } => {
            if (max - min == 0 && !zeroOut) max += rangeOffset;
            return zeroOut ? { min: 0, max: 0 } : { min, max };
        };

        const gapRange = (min: number, max: number): { min: number; max: number } => {
            if (max - min <= rangeOffset) return { min: 0, max: 0 };
            if (max != rangeMax) max -= rangeOffset;
            return { min, max };
        };

        const veryLow = toRange(
            rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryLow.setPoint),
            !fcuAlarms.alarmSettingVeryLow.enable
        );
        const low = toRange(
            fcuAlarms.alarmSettingVeryLow.enable ? veryLow.max : rangeMin,
            this.scaleValue(channel, fcuAlarms.alarmSettingLow.setPoint),
            !fcuAlarms.alarmSettingLow.enable
        );
        const veryHigh = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingVeryHigh.setPoint),
            rangeMax,
            !fcuAlarms.alarmSettingVeryHigh.enable
        );
        const high = toRange(
            this.scaleValue(channel, fcuAlarms.alarmSettingHigh.setPoint),
            fcuAlarms.alarmSettingVeryHigh.enable ? veryHigh.min : rangeMax,
            !fcuAlarms.alarmSettingHigh.enable
        );

        const hasNormalRange = fcuAlarms.normalRange.min !== 0 || fcuAlarms.normalRange.max !== 0;
        const normal = toRange(
            this.scaleValue(channel, fcuAlarms.normalRange.min),
            this.scaleValue(channel, fcuAlarms.normalRange.max),
            !hasNormalRange
        );
        const withoutNormalRange = ((high.min || rangeMax) - (low.max || rangeMin)) / 2;

        const lowToNormalGap = gapRange(
            low.max || rangeMin,
            hasNormalRange ? normal.min : (low.max || rangeMin) + withoutNormalRange
        );

        const normalToHighGap = gapRange(hasNormalRange ? normal.max : lowToNormalGap.max, high.min || rangeMax);

        rangeMap.set(FlagOption.HIGH_HIGH, veryHigh);
        rangeMap.set(FlagOption.HIGH, high);
        rangeMap.set('normalToHigh', normalToHighGap);
        rangeMap.set(FlagOption.NORMAL, normal);
        rangeMap.set('lowToNormal', lowToNormalGap);
        rangeMap.set(FlagOption.LOW, low);
        rangeMap.set(FlagOption.LOW_LOW, veryLow);

        return rangeMap;
    }

    updateDisplay(foreignObject, channel: Channel, barSetting: BarSetting, systemColors: SystemColor[]) {
        const eventWithUnits = new CustomEvent(this.updateDisplayEventName, {
            detail: { channel, barSetting, systemColors },
        });
        foreignObject.dispatchEvent(eventWithUnits);
    }

    setUnitsVisible(foreignObject: Element, visible: boolean) {
        const labelsContainer = foreignObject.querySelector('[labelsContainer="true"]') as HTMLElement;
        if (!labelsContainer) return;
        labelsContainer.style.display = visible ? 'flex' : 'none';
    }

    setValueVisible(foreignObject: Element, visible: boolean) {
        const valueContainer = foreignObject.querySelector('[valueContainer="true"]') as HTMLElement;
        if (!valueContainer) return;
        valueContainer.style.display = visible ? 'flex' : 'none';
    }

    setNameVisible(foreignObject: Element, visible: boolean) {
        const channelNameContainer = foreignObject.querySelector('[channelNameContainer="true"]') as HTMLElement;
        if (!channelNameContainer) return;
        channelNameContainer.style.display = visible ? 'flex' : 'none';
    }

    addEventListeners(foreignObject) {
        const rangesContainer = foreignObject.querySelector('[rangesContainer="true"]') as HTMLElement;
        const valueContainer = foreignObject.querySelector('[valueContainer="true"]') as HTMLElement;
        const channelNameContainer = foreignObject.querySelector('[channelNameContainer="true"]') as HTMLElement;
        const linearGaugeRoot = foreignObject.querySelector('[linearGaugeRoot="true"]') as HTMLElement;
        const indicatorPointer = foreignObject.querySelector('[indicatorPointer="true"]') as HTMLElement;

        foreignObject.addEventListener(this.updateDisplayEventName, (event: any) => {
            const channel: Channel = event.detail.channel;
            const barSetting: BarSetting = event.detail.barSetting;
            const systemColors: SystemColor[] = event.detail.systemColors;

            if (channel) {
                channelNameContainer.textContent = channel.opsData.name;
                valueContainer.textContent = `- (${channel.opsData.measurementUnit})`;
            } else {
                channelNameContainer.textContent = 'Channel';
                valueContainer.textContent = `- (Units)`;
            }

            if (barSetting) {
                linearGaugeRoot.setAttribute(ElementAttribute.barScaleType, barSetting?.scale);
                linearGaugeRoot.setAttribute(ElementAttribute.barIntervals, barSetting?.interval.toString());
            }

            this.applyRanges(rangesContainer, channel, systemColors);
            this.applyTicks(foreignObject, channel, barSetting);
            this.styleLabelsStyle(foreignObject);
        });

        // foreignObject.addEventListener(this.updateValueEventName, (event: any) => {
        //     indicatorPointer.style.top = event.detail.percentage;
        //     indicatorPointer.style.backgroundColor = event.detail.color;
        // });
    }
}
